import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { initializeStores } from './utils/storeUtils';
import { subscribeToEventSession } from './services/sessionSync';
import useEventStore from './stores/eventStore';
import HomeScreen from './components/home/HomeScreen';
import PersonalListenScreen from './components/home/PersonalListenScreen';
import JoinScreen from './components/join/JoinScreen';
import PartyScreen from './components/party/PartyScreen';
import GoogleRedirectHandler from './components/auth/GoogleRedirectHandler';
import { initializeAuthSession, watchAuthState } from './services/authSession';
import { loadSessionEventId } from './services/sessionRef';
import { buildJoinPath, parseJoinCodeFromSearch } from './utils/joinLink';
import './App.css';

/**
 * Accepts `?join=CODE` as an alternative to `/join/CODE`, so a shared link
 * still works on static hosting without an SPA rewrite rule.
 */
function JoinQueryFallback({ children }) {
  const location = useLocation();
  const code = parseJoinCodeFromSearch(location.search);

  if (code && !location.pathname.startsWith('/join')) {
    return <Navigate to={buildJoinPath(code)} replace />;
  }

  return children;
}

/**
 * `/party` with no code is the only place the stored last-event pointer is
 * used. Opening `/` always shows the dashboard.
 */
function LastPartyRedirect() {
  const eventId = loadSessionEventId();
  return <Navigate to={eventId ? `/party/${eventId}` : '/'} replace />;
}

function App() {
  const eventData = useEventStore(state => state.eventData);
  const eventId = eventData?.id ?? null;
  const [authReady, setAuthReady] = React.useState(false);
  const [authError, setAuthError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const unwatch = watchAuthState();

    (async () => {
      try {
        await initializeAuthSession();
        if (!cancelled) {
          setAuthError(null);
          setAuthReady(true);
        }
      } catch (error) {
        console.error('Anonymous auth failed:', error);
        if (!cancelled) {
          setAuthError('Could not start an anonymous session. Please refresh and try again.');
          setAuthReady(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      unwatch?.();
    };
  }, []);

  React.useEffect(() => {
    if (!authReady) return undefined;

    if (!eventId) {
      initializeStores();
      return undefined;
    }

    const unsubscribeSession = subscribeToEventSession(eventId);

    return () => {
      unsubscribeSession();
    };
  }, [eventId, authReady]);

  if (!authReady && !authError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center text-white">
        <p>Connecting your session…</p>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center text-white p-4">
        <p>{authError}</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="top-center" />
      <JoinQueryFallback>
        <GoogleRedirectHandler />
        <Routes>
          <Route path="/" element={<HomeScreen />} />
          <Route path="/listen" element={<PersonalListenScreen />} />
          <Route path="/join/:eventCode" element={<JoinScreen />} />
          <Route path="/party/:eventCode" element={<PartyScreen />} />
          <Route path="/party" element={<LastPartyRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </JoinQueryFallback>
    </BrowserRouter>
  );
}

export default App;
